import React from 'react';
import { getIntl } from '../../../../utils/intl';
import ExpandableInformationSection from './ExpandableInformationSection';
import ExpandableInformationSubsection from './ExpandableInformationSubsection';
import Text from '../../../v2BaseComponents/Text';
import Icon, { IconName } from '../../../v2BaseComponents/Icon';
import Stack from '../../../v2BaseComponents/Stack';
import { getSpacing } from '../../../../utils/spacing';
import { View } from 'react-native';

const intl = getIntl('DataSafetySection', {
    dataSafety: 'Data Safety',
    paragraph1:
        'Safety starts with understanding how your data is collected and shared. Data privacy and security practices may vary based on your use, region, and age.',
    paragraph2:
        'Primary Record is classified as a consumer health application under the FTC as part of the 21st Century Curse Act. Primary Record is not classified as a HIPAA covered entity.',
    dataSharedTitle: 'Data Shared',
    dataSharedDescription:
        'No application data is shared with third parties for marketing or sales purposes without explicit user consent.',
    appActivityTitle: 'App Activity',
    appActivityDescription:
        'App sessions, app interactions, and user-generated content (optional) may be collected and used to support app functionality enhancements.',
    requiredPersonalInfoTitle: 'Required Personal Info',
    requiredPersonalInfoDescription:
        'Legal name, email, and date of birth are collected for authentication, fraud prevention, security, and account management.',
    deviceOrOtherIdsTitle: 'Device or Other IDs',
    deviceOrOtherIdsDescription:
        'Device ID, device type, and browser type may be collected and used for customer support and app functionality enhancements.',
    filesAndDocsTitle: 'Files and Docs (optional)',
    filesAndDocsDescription:
        'All uploaded files, images, and/or documents are to be used by the user for application functionality.',
    appInfoAndPerformanceTitle: 'App Info and Performance',
    appInfoAndPerformanceDescription:
        'Crash logs and other diagnostics may be collected and are used for operational analytics.',
    medicalAndHealthInfoTitle: 'Medical and Health Info',
    medicalAndHealthInfoDescription:
        'Medications, conditions, allergies, encounters, procedures, visits, vaccinations, and labs can all be optionally stored in the application. Personal health and medical information is not shared without users express opt-in consent.',
    dataSecurityTitle: 'Data Security',
    dataSecurityDescription: 'Data is encrypted in transit and at rest. All data access is monitored.',
    dataDeletionTitle: 'Data Deletion',
    dataDeletionDescription: 'You can delete all data collected in the app.',
    shareText:
        'This app shares personal info and user activity exclusively with internal business systems essential for providing and improving the Primary Record service. These systems support operations such as customer support, service delivery, and app functionality enhancements, ensuring an optimal customer experience. No data is shared with third parties for marketing or sales purposes without explicit user consent.',
    collectText: 'This application collects personal info, health, medical, and user activity information.',
    encryptionText: 'Data is encrypted in transit and at rest and all access to data is monitored.',
    deletionText: 'Users can request access to all data collected by the app and its deletion..',
});

const iconTexts: { iconName: IconName; text: string }[] = [
    { iconName: 'Share2', text: intl('shareText') },
    { iconName: 'UploadCloud', text: intl('collectText') },
    { iconName: 'Lock', text: intl('encryptionText') },
    { iconName: 'Trash2', text: intl('deletionText') },
];

const sections = [
    {
        title: intl('dataSharedTitle'),
        contents: [intl('dataSharedDescription')],
    },
    {
        title: intl('appActivityTitle'),
        contents: [intl('appActivityDescription')],
    },
    {
        title: intl('requiredPersonalInfoTitle'),
        contents: [intl('requiredPersonalInfoDescription')],
    },
    {
        title: intl('deviceOrOtherIdsTitle'),
        contents: [intl('deviceOrOtherIdsDescription')],
    },
    {
        title: intl('filesAndDocsTitle'),
        contents: [intl('filesAndDocsDescription')],
    },
    {
        title: intl('appInfoAndPerformanceTitle'),
        contents: [intl('appInfoAndPerformanceDescription')],
    },
    {
        title: intl('medicalAndHealthInfoTitle'),
        contents: [intl('medicalAndHealthInfoDescription')],
    },
    {
        title: intl('dataSecurityTitle'),
        contents: [intl('dataSecurityDescription')],
    },
    {
        title: intl('dataDeletionTitle'),
        contents: [intl('dataDeletionDescription')],
    },
];

const DataSafetySection: React.FC = () => {
    const iconTextsComponent = (
        <Stack
            space={getSpacing(4)}
            vertical
            style={{
                paddingVertical: getSpacing(2),
            }}
        >
            {iconTexts.map((iconText, index) => (
                <Stack key={index} space={getSpacing(4)} horizontal>
                    <View
                        style={{
                            width: 24,
                            height: 24,
                        }}
                    >
                        <Icon name={iconText.iconName} size="homepage-icon" />
                    </View>
                    <Text
                        variant="h5"
                        color="Gray-Text"
                        style={{
                            lineHeight: 26,
                            flex: 1,
                        }}
                    >
                        {iconText.text}
                    </Text>
                </Stack>
            ))}
        </Stack>
    );

    return (
        <ExpandableInformationSection
            title={intl('dataSafety')}
            renderCollapsedContent={() => (
                <Stack space={getSpacing(4)} vertical>
                    <Text
                        variant="h5"
                        color="Gray-Text"
                        style={{
                            lineHeight: 26,
                        }}
                    >
                        {intl('paragraph1')}
                    </Text>
                    {iconTextsComponent}
                </Stack>
            )}
            renderExpandedContent={() => (
                <Stack space={getSpacing(4)} vertical>
                    <Text
                        variant="h5"
                        color="Gray-Text"
                        style={{
                            lineHeight: 26,
                        }}
                    >
                        {intl('paragraph1')}
                    </Text>
                    <Text
                        variant="h5"
                        color="Gray-Text"
                        style={{
                            lineHeight: 26,
                        }}
                    >
                        {intl('paragraph2')}
                    </Text>
                    {sections.map((section, index) => (
                        <ExpandableInformationSubsection
                            key={index}
                            title={section.title}
                            contents={section.contents}
                        />
                    ))}
                    {iconTextsComponent}
                </Stack>
            )}
        />
    );
};

export default React.memo(DataSafetySection);
